'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { FilterConfig, FilterValue } from '../../components/filter-bar';
import {
  CREATED_AT_DATE_FILTER_ID,
  CREATED_AT_DATE_PRESET,
} from './created-at-date-filter.constants';
import { CreatedAtDateFilterDialog } from './CreatedAtDateFilterDialog';
import {
  buildSinceLoginDateFilter,
  didSelectNewCreatedAtDatePreset,
  formatCreatedAtDateFilterLabel,
  getClearedCreatedAtDateFilterValue,
  getDefaultCreatedAtDateFilterValue,
  isCreatedAtDateFilterActive,
  isCreatedAtDatePreset,
  type CreatedAtDateFilterValue,
} from './created-at-date-filter.utils';

type UiCreatedAtDatePreset =
  | typeof CREATED_AT_DATE_PRESET.DAYS_7
  | typeof CREATED_AT_DATE_PRESET.DAYS_30
  | typeof CREATED_AT_DATE_PRESET.DAYS_90
  | typeof CREATED_AT_DATE_PRESET.SINCE_LOGIN
  | typeof CREATED_AT_DATE_PRESET.CUSTOM;

const isUiCreatedAtDatePreset = (
  value: string,
): value is UiCreatedAtDatePreset =>
  value === CREATED_AT_DATE_PRESET.DAYS_7 ||
  value === CREATED_AT_DATE_PRESET.DAYS_30 ||
  value === CREATED_AT_DATE_PRESET.DAYS_90 ||
  value === CREATED_AT_DATE_PRESET.SINCE_LOGIN ||
  value === CREATED_AT_DATE_PRESET.CUSTOM;

type UseCreatedAtDateFilterParams = {
  filter: CreatedAtDateFilterValue;
  onFilterChange: (filter: CreatedAtDateFilterValue) => void;
  formatDate: (date: Date) => string;
  sinceLoginStartDate?: Date | null;
  dataTestid?: string;
};

export function useCreatedAtDateFilter({
  filter,
  onFilterChange,
  formatDate,
  sinceLoginStartDate,
  dataTestid,
}: UseCreatedAtDateFilterParams) {
  const t = useTranslations('designSystem.createdAtDateFilter');

  const [isCustomRangeOpen, setCustomRangeOpen] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState<Date | null>(null);
  const [draftEndDate, setDraftEndDate] = useState<Date | null>(null);
  const [customRangeError, setCustomRangeError] = useState<string | null>(null);

  const presetLabels = useMemo<Record<UiCreatedAtDatePreset, string>>(
    () => ({
      [CREATED_AT_DATE_PRESET.DAYS_7]: t('presets.7d'),
      [CREATED_AT_DATE_PRESET.DAYS_30]: t('presets.30d'),
      [CREATED_AT_DATE_PRESET.DAYS_90]: t('presets.90d'),
      [CREATED_AT_DATE_PRESET.SINCE_LOGIN]: t('presets.sinceLogin'),
      [CREATED_AT_DATE_PRESET.CUSTOM]: t('presets.custom'),
    }),
    [t],
  );

  const selectedFilterLabel = useMemo(
    () =>
      formatCreatedAtDateFilterLabel({
        filter,
        formatDate,
        presetLabels,
      }),
    [filter, formatDate, presetLabels],
  );

  const clearFilter = useCallback(() => {
    onFilterChange(getClearedCreatedAtDateFilterValue());
  }, [onFilterChange]);

  const filterConfig = useMemo<FilterConfig>(
    () => ({
      id: CREATED_AT_DATE_FILTER_ID,
      placeholder: t('placeholder'),
      required: false,
      onClear: clearFilter,
      ...(dataTestid ? { dataTestid } : {}),
      items: [
        {
          label: presetLabels[CREATED_AT_DATE_PRESET.DAYS_7],
          value: CREATED_AT_DATE_PRESET.DAYS_7,
        },
        {
          label: presetLabels[CREATED_AT_DATE_PRESET.DAYS_30],
          value: CREATED_AT_DATE_PRESET.DAYS_30,
        },
        {
          label: presetLabels[CREATED_AT_DATE_PRESET.DAYS_90],
          value: CREATED_AT_DATE_PRESET.DAYS_90,
        },
        ...(sinceLoginStartDate
          ? [
              {
                label: presetLabels[CREATED_AT_DATE_PRESET.SINCE_LOGIN],
                value: CREATED_AT_DATE_PRESET.SINCE_LOGIN,
              },
            ]
          : []),
        {
          label: presetLabels[CREATED_AT_DATE_PRESET.CUSTOM],
          value: CREATED_AT_DATE_PRESET.CUSTOM,
        },
      ],
      resolveBadgeText: ({ selectedValues }: { selectedValues: string[] }) => {
        const selectedPreset = selectedValues[0];

        if (!selectedPreset || !isCreatedAtDatePreset(selectedPreset)) {
          return null;
        }

        if (selectedPreset === CREATED_AT_DATE_PRESET.CUSTOM) {
          return selectedFilterLabel;
        }

        if (isUiCreatedAtDatePreset(selectedPreset)) {
          return presetLabels[selectedPreset];
        }

        return selectedFilterLabel;
      },
    }),
    [
      clearFilter,
      dataTestid,
      presetLabels,
      selectedFilterLabel,
      sinceLoginStartDate,
      t,
    ],
  );

  const filterValue = useMemo<FilterValue>(() => {
    if (!isCreatedAtDateFilterActive({ filter })) {
      return [];
    }

    const { preset } = filter;

    if (!preset) {
      return [];
    }

    return [{ id: CREATED_AT_DATE_FILTER_ID, value: [preset] }];
  }, [filter]);

  const openCustomRange = useCallback(() => {
    setDraftStartDate(filter.createdStartDate);
    setDraftEndDate(filter.createdEndDate);
    setCustomRangeError(null);
    setCustomRangeOpen(true);
  }, [filter.createdEndDate, filter.createdStartDate]);

  const closeCustomRange = useCallback(() => {
    setCustomRangeOpen(false);
    setDraftStartDate(null);
    setDraftEndDate(null);
    setCustomRangeError(null);
  }, []);

  const handleCustomApply = useCallback(() => {
    if (!draftStartDate || !draftEndDate) {
      setCustomRangeError(t('customRange.validation.required'));

      return;
    }

    if (draftEndDate.getTime() < draftStartDate.getTime()) {
      setCustomRangeError(t('customRange.validation.invalidRange'));

      return;
    }

    onFilterChange({
      preset: CREATED_AT_DATE_PRESET.CUSTOM,
      createdStartDate: draftStartDate,
      createdEndDate: draftEndDate,
      displayLabel: null,
    });
    setCustomRangeOpen(false);
  }, [draftEndDate, draftStartDate, onFilterChange, t]);

  const handleStartDateChange = useCallback((value: string) => {
    setDraftStartDate(value ? new Date(value) : null);
    setCustomRangeError(null);
  }, []);

  const handleEndDateChange = useCallback((value: string) => {
    setDraftEndDate(value ? new Date(value) : null);
    setCustomRangeError(null);
  }, []);

  const handleFilterChange = useCallback(
    (nextValue: FilterValue) => {
      const entry = nextValue.find(
        (value) => value.id === CREATED_AT_DATE_FILTER_ID,
      );
      const nextPreset = entry?.value[0];

      if (!entry || entry.value.length === 0 || !nextPreset) {
        clearFilter();

        return;
      }

      if (!isCreatedAtDatePreset(nextPreset)) {
        return;
      }

      if (nextPreset === CREATED_AT_DATE_PRESET.CUSTOM) {
        if (
          didSelectNewCreatedAtDatePreset({
            filterValue: nextValue,
            previousPreset: filter.preset ?? undefined,
          })
        ) {
          openCustomRange();
        }

        return;
      }

      if (nextPreset === CREATED_AT_DATE_PRESET.SINCE_LOGIN) {
        if (!sinceLoginStartDate) {
          return;
        }

        onFilterChange(buildSinceLoginDateFilter({ sinceLoginStartDate }));

        return;
      }

      if (nextPreset === filter.preset) {
        return;
      }

      onFilterChange({
        preset: nextPreset,
        createdStartDate: null,
        createdEndDate: null,
        displayLabel: null,
      });
    },
    [
      clearFilter,
      filter.preset,
      onFilterChange,
      openCustomRange,
      sinceLoginStartDate,
    ],
  );

  const resetFilter = useCallback(() => {
    onFilterChange(getDefaultCreatedAtDateFilterValue());
  }, [onFilterChange]);

  const handleCustomRangeOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        closeCustomRange();
      }
    },
    [closeCustomRange],
  );

  const customRangeDialog = (
    <CreatedAtDateFilterDialog
      isOpen={isCustomRangeOpen}
      startDate={draftStartDate}
      endDate={draftEndDate}
      errorMessage={customRangeError}
      onOpenChange={handleCustomRangeOpenChange}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      onCancel={closeCustomRange}
      onApply={handleCustomApply}
    />
  );

  return {
    filterConfig,
    filterValue,
    onFilterChange: handleFilterChange,
    customRangeDialog,
    resetFilter,
    clearFilter,
  };
}

export type CreatedAtDateFilterControls = ReturnType<
  typeof useCreatedAtDateFilter
>;
